import React, { useState, useRef, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { cn } from '../utils/cn';

const StickySplitBackground = ({
  leftImage,
  rightImage,
  title,
  description,
  className = '',
  showOverlay = false
}) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [imageOrientation, setImageOrientation] = useState('landscape');
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      setShouldLoad(true);
    }
  }, [inView]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check image orientation
  useEffect(() => {
    if (leftImage) {
      const img = new Image();
      img.onload = () => {
        setImageOrientation(img.width >= img.height ? 'landscape' : 'portrait');
      };
      img.src = leftImage;
    }
  }, [leftImage]);

  // Function to optimize Contentful image URLs
  const optimizeImageUrl = (url) => {
    if (!url.includes('ctfassets.net')) return url;
    
    // Add query parameters for optimization
    const optimizedUrl = new URL(url);
    optimizedUrl.searchParams.set('w', isMobile ? '640' : '1200'); // Lower resolution for mobile
    optimizedUrl.searchParams.set('q', isMobile ? '70' : '80'); // Lower quality for mobile
    optimizedUrl.searchParams.set('fm', 'webp'); // Use WebP format
    return optimizedUrl.toString();
  };

  return (
    <section 
      ref={ref}
      className="w-full relative"
      style={{
        height: isMobile ? '300px' : '500px',
        backgroundColor: 'black',
        width: '100%',
        zIndex: -2
      }}
    >
      {/* Left Image */}
      <div 
        className={cn(
          "absolute top-0 h-full",
          isMobile ? "w-full left-0" : "w-1/2 left-0"
        )} 
        style={{ 
          position: 'fixed', 
          maxWidth: isMobile ? '100vw' : '50vw', 
          zIndex: -2 
        }}
      >
        {shouldLoad && (
          <img 
            src={optimizeImageUrl(leftImage)} 
            alt="Left Image" 
            className={cn(
              "w-full h-full object-cover",
              imageOrientation === 'portrait' ? "object-center" : "object-cover"
            )}
            style={{ 
              maxWidth: isMobile ? '100vw' : '50vw', 
              maxHeight: '100vh' 
            }}
            loading="lazy"
          />
        )}
      </div>

      {/* Right Image - Only show on desktop */}
      {!isMobile && (
        <div 
          className="absolute top-0 right-0 w-1/2 h-full" 
          style={{ position: 'fixed', maxWidth: '50vw', zIndex: -2 }}
        >
          {shouldLoad && (
            <img 
              src={optimizeImageUrl(rightImage)} 
              alt="Right Image" 
              className="w-full h-full object-cover" 
              style={{ maxWidth: '50vw', maxHeight: '100vh' }}
              loading="lazy"
            />
          )}
        </div>
      )}

      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className={cn(
          "text-center text-white px-4",
          isMobile ? "max-w-[300px]" : "max-w-2xl"
        )}>
          <h2 className={cn(
            "font-bold mb-4",
            isMobile ? "text-3xl" : "text-4xl"
          )}>{title}</h2>
          <p className={cn(
            isMobile ? "text-base" : "text-lg"
          )}>{description}</p>
        </div>
      </div>
    </section>
  );
};

export default StickySplitBackground; 